import { DevMode } from '@chipinside/devmode'
import { Stack } from '@mui/material'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { isEmpty } from 'lodash'
import { useEffect, useLayoutEffect, useMemo } from 'react'
import { CSSProperties } from 'react'
import { useLocation } from 'react-router-dom'

import { HelpPages } from '#/components/HelpPages'
import Breadcrumbs from '#/components/PageHeader/Breadcrumbs'
import { usePageHeader } from '#/contexts/PageHeader'
import { useCompanyName } from '#/store/ducks/company'
import { useFarmName } from '#/store/ducks/farm'
import { usePages } from '#/store/services/Pages/usePages'
import LineClamp from '#/styles/LineClamp'

const Title = styled('div')(({ theme }) => ({
  ...(LineClamp as CSSProperties),
  color: theme.palette.grey[900],
  fontSize: '1.3em',
  fontWeight: 'bold',
  lineHeight: '1.4em',
  margin: '0',
  maxWidth: 'unset',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1em',
    lineHeight: '1.2em',
  },
  WebkitLineClamp: 1,
}))

const Subtitle = styled('div')({
  fontSize: '1.3em',
  margin: '0',
  padding: '4px 0',
})

const Container = styled('div')({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  padding: '8px 0 16px',
})

export type Crumbs = {
  name?: string | null
  link?: string | null
}

export type CrumbsProps = {
  crumbs: Crumbs[]
}

/**
 * Cabeçalho global de página, alimentado pelo contexto PageHeader.
 * Renderiza em toda tela autenticada, exceto quando `hidden: true` é
 * definido via usePageHeader().
 */
export function PageHeader() {
  const { pageHeader, resetPageHeader } = usePageHeader()
  const { pathname } = useLocation()
  const pages = usePages()
  const farmName = useFarmName()
  const company = useCompanyName()

  const page = useMemo(() => {
    const current = pages.getCurrent({ path: pathname })
    return current?.enabled ? current : undefined
  }, [pages, pathname])

  const { hidden } = pageHeader ?? {}

  const title = pageHeader.title ?? page?.title
  const subtitle = pageHeader.subtitle ?? page?.subtitle
  const description = pageHeader.description ?? page?.description

  const crumbs = pages.extractCrumbs({
    crumbMap: pageHeader?.crumbMap,
    page,
  })

  const name = farmName ?? company

  // Limpa os valores ao trocar de rota para não vazarem para a próxima tela
  useLayoutEffect(() => {
    return () => resetPageHeader()
  }, [pathname, resetPageHeader])

  useEffect(() => {
    if (!isEmpty(crumbs)) {
      document.title = `COWMED - ${crumbs.map(crumb => crumb.name)?.join(' - ')}`
    } else {
      document.title = name ? `COWMED - ${name}` : 'COWMED'
    }
  }, [crumbs, farmName])

  if (hidden) {
    return null
  }

  return (
    <Container style={{ gap: 10 }} id="headercrumbs">
      <div style={{ alignItems: 'center', display: 'flex', gap: 8, marginRight: 8 }}>
        {page && (
          <DevMode
            code={page}
            items={[
              {
                title: 'ID',
                value: page?.id,
              },
              {
                title: 'Permissão de visualização',
                value: page.permissions,
              },
            ]}
          />
        )}
        <Stack>
          <Stack sx={{ alignItems: 'center', columnGap: 1, flexDirection: 'row' }}>
            {title && <Title id="pagetitle">{title}</Title>}
            {subtitle && (
              <>
                <span>-</span>
                <Subtitle>{subtitle}</Subtitle>
              </>
            )}
          </Stack>
          {description && (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', ...LineClamp, maxWidth: '100%', WebkitLineClamp: 1 }}
            >
              {description}
            </Typography>
          )}
        </Stack>
        {/*HELP*/}
        <HelpPages />
        {/* {children} */}
      </div>
      <Breadcrumbs crumbs={crumbs} />
    </Container>
  )
}
